/**
 * Manual FLIP-style morph for the recipe card → hero image transition.
 *
 * Why we don't use the View Transitions API: we tried, see the long
 * postmortem in this repo's git log. The browser suspends timers inside
 * `document.startViewTransition`'s callback, so we can't await the new
 * route's RSC commit before snapshotting — the new hero element doesn't
 * exist yet, so the API has nothing to morph and falls back to no-op.
 *
 * What we do instead:
 *   1. Capture the card image's screen rect on click.
 *   2. Build a floating <div> with the same image, fixed-positioned,
 *      stacked over everything (z-index 9999).
 *   3. CSS-transition it from the source rect to an estimated hero rect.
 *   4. While it animates, poll for the real `<div data-morph-target={slug}>`
 *      to land in the new page. When it does, refine the animation target
 *      to its measured rect — keeps the clone's final position pixel-perfect
 *      against whatever the hero actually rendered as.
 *   5. After the animation finishes, fade the clone out — the real hero
 *      (already in the DOM at this point thanks to OptimisticHero) is
 *      revealed underneath.
 *
 * The clone is the only thing the user sees during the morph; the rest of
 * the new page can render however slowly behind it.
 */

const ANIMATION_MS = 350;
const FADE_OUT_MS = 180;
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
// How long after animation start we'll keep polling for the real hero.
// After this we just fade out at our estimated position.
const HERO_POLL_BUDGET_MS = 1500;
// Border radius the clone starts with (matches the card's --r-3-ish curve).
const SOURCE_BORDER_RADIUS = 12;

interface MorphOptions {
  sourceRect: DOMRect;
  imageUrl: string | null;
  slug: string;
}

export function startHeroMorph({ sourceRect, imageUrl, slug }: MorphOptions): void {
  if (typeof window === 'undefined') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Cancel any in-flight morph clone — user clicked again before this one finished.
  document.querySelectorAll('[data-morph-clone]').forEach((el) => el.remove());

  const clone = document.createElement('div');
  clone.dataset.morphClone = slug;
  // Initial clone style — fixed at source position, will animate via transform.
  // Using transform (compositor-only) instead of top/left/width/height to avoid
  // layout repaints. backgroundColor is transparent to avoid dark-mode flash.
  Object.assign(clone.style, {
    position: 'fixed',
    top: `${sourceRect.top}px`,
    left: `${sourceRect.left}px`,
    width: `${sourceRect.width}px`,
    height: `${sourceRect.height}px`,
    transform: 'translate(0, 0) scale(1, 1)',
    transformOrigin: 'top left',
    willChange: 'transform',
    borderRadius: `${SOURCE_BORDER_RADIUS}px`,
    overflow: 'hidden',
    zIndex: '9999',
    pointerEvents: 'none',
    backgroundColor: 'transparent',
    transition: [
      `transform ${ANIMATION_MS}ms ${EASE}`,
      `border-radius ${ANIMATION_MS}ms ${EASE}`,
    ].join(', '),
  } satisfies Partial<CSSStyleDeclaration>);

  if (imageUrl) {
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = '';
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
    clone.appendChild(img);
  }

  document.body.appendChild(clone);
  // Force layout flush so the browser registers the initial rect before we
  // change it — otherwise CSS transitions skip and the clone teleports.
  clone.getBoundingClientRect();

  // Estimated target — refined when the real hero shows up.
  const estimated = estimateHeroRect();
  let appliedTarget: { top: number; left: number; width: number; height: number } = estimated;

  requestAnimationFrame(() => {
    applyTarget(clone, sourceRect, estimated);
  });

  // Poll for the real hero element so we can correct the final rect.
  const pollStart = performance.now();
  let heroFound = false;
  const poll = () => {
    if (heroFound) return;
    if (performance.now() - pollStart > HERO_POLL_BUDGET_MS) return;

    const hero = document.querySelector<HTMLElement>(`[data-morph-target="${cssEscape(slug)}"]`);
    if (hero) {
      const rect = hero.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        heroFound = true;
        appliedTarget = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
        applyTarget(clone, sourceRect, appliedTarget);
        return;
      }
    }
    requestAnimationFrame(poll);
  };
  requestAnimationFrame(poll);

  // After the move animation finishes, fade out and remove. The real hero
  // is sitting underneath us at this point.
  window.setTimeout(() => {
    clone.style.transition = `opacity ${FADE_OUT_MS}ms ease`;
    clone.style.opacity = '0';
    window.setTimeout(() => clone.remove(), FADE_OUT_MS + 50);
  }, ANIMATION_MS + 50);
}

function applyTarget(
  clone: HTMLElement,
  sourceRect: DOMRect,
  target: { top: number; left: number; width: number; height: number },
): void {
  const dx = target.left - sourceRect.left;
  const dy = target.top - sourceRect.top;
  const sx = target.width / sourceRect.width;
  const sy = target.height / sourceRect.height;
  clone.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
  clone.style.borderRadius = '0';
}

/**
 * Best-guess hero rect computed from layout constants — used to start the
 * animation before the new page has rendered. Refined as soon as the real
 * `[data-morph-target]` element appears.
 *
 * Layout assumptions (from recipe-detail-client.module.css and recipe-hero.module.css):
 *   - .article max-width: 1280px, centered
 *   - padding: 24/16 (mobile), 32/24 (desktop)
 *   - hero grid: 1fr (mobile), 1fr 1fr (desktop ≥768px), gap ~32px
 *   - .imageWrapper aspect-ratio: 4/3
 */
function estimateHeroRect(): { top: number; left: number; width: number; height: number } {
  const vw = window.innerWidth;
  const isDesktop = vw >= 768;
  const layoutMax = 1280;
  const containerWidth = Math.min(vw, layoutMax);
  const containerLeft = (vw - containerWidth) / 2;
  const padX = isDesktop ? 24 : 16;
  const padTop = isDesktop ? 32 : 24;
  // Matches --header-height (56px). Was 64 — a stale constant missed when the
  // header shrank 64→56, leaving the morph's initial estimate 8px too low
  // until the poll corrected it.
  const headerHeight = 56;
  const innerWidth = containerWidth - padX * 2;
  const heroWidth = isDesktop ? (innerWidth - 32) / 2 : innerWidth;
  const heroHeight = heroWidth * 0.75;
  return {
    top: headerHeight + padTop,
    left: containerLeft + padX,
    width: heroWidth,
    height: heroHeight,
  };
}

function cssEscape(s: string): string {
  // Conservative escape for use inside an attribute selector value.
  // Slugs are kebab-case ASCII so this is mostly defensive.
  return s.replace(/["\\]/g, '\\$&');
}
